/**
 * overBodySerialize — turn a structured `OverBodySpec` back into canonical
 * `OVER (…)`-body text (`PARTITION BY … ORDER BY … ROWS BETWEEN … AND …
 * EXCLUDE …`). Pure, no React. Paired with `overBodyParse.ts` under the round-
 * trip invariant documented in `overBodySpec.ts`.
 *
 * When a spec is flagged `parseable: false`, the original `raw` bytes are
 * returned unchanged so a non-standard body is reproduced exactly.
 */
import type { FrameBound, FrameSpec, OverBodySpec, ParsedOrderItem } from './overBodySpec';

export function serializeOverBody(spec: OverBodySpec): string {
  // Non-standard / unparseable body: reproduce the user's exact bytes.
  if (!spec.parseable) return spec.raw;

  const parts: string[] = [];
  if (spec.partitionBy.length > 0) {
    parts.push(`PARTITION BY ${spec.partitionBy.join(', ')}`);
  }
  if (spec.orderBy.length > 0) {
    parts.push(`ORDER BY ${spec.orderBy.map(serializeOrderItem).join(', ')}`);
  }
  if (spec.frame) {
    const frameText = serializeFrame(spec.frame);
    parts.push(spec.frameExclusion ? `${frameText} ${spec.frameExclusion}` : frameText);
  }
  return parts.join(' ');
}

function serializeOrderItem(item: ParsedOrderItem): string {
  let out = item.expression;
  if (item.direction) out += ` ${item.direction}`;
  if (item.nulls) out += ` NULLS ${item.nulls}`;
  return out;
}

function serializeFrame(frame: FrameSpec): string {
  const unit = frame.unit.toUpperCase();
  if (frame.end === null) {
    return `${unit} ${serializeBound(frame.start)}`;
  }
  return `${unit} BETWEEN ${serializeBound(frame.start)} AND ${serializeBound(frame.end)}`;
}

function serializeBound(bound: FrameBound): string {
  switch (bound.kind) {
    case 'unbounded-preceding':
      return 'UNBOUNDED PRECEDING';
    case 'unbounded-following':
      return 'UNBOUNDED FOLLOWING';
    case 'current-row':
      return 'CURRENT ROW';
    case 'n-preceding':
      return `${bound.n ?? 0} PRECEDING`;
    case 'n-following':
      return `${bound.n ?? 0} FOLLOWING`;
  }
}
